import { Injectable } from '@angular/core';
import { GraphQLQuery } from '@aws-amplify/api';
import { Employee } from '@core/types/core';
import { API } from 'aws-amplify';
import { GetEmployeeQueryVariables } from 'kms-amplify/src';
import { BehaviorSubject } from 'rxjs';
import { AuthenticateService } from './authenticate.service';
import {
  EmployeeIdsByUserId,
  GetEmployeeDetailQuery,
  ListEmployeesWithAttributesQuery,
  ListEmployeesWithAttributesQueryVariables,
} from '@core/graphql/custom.types';
import {
  getEmployeeDetail,
  getEmployeeIdsByUserId,
  listEmployeesWithAttributes,
} from '@core/graphql/custom.queries';
import { DataMappingService } from './data-mapping.service';

export type ListEmployeeParam = {
  limit?: number;
  searchKeyword?: string;
  searchOrganizationUnit?: string;
  nextToken?: string | null;
};

export type ListEmployeeResult = {
  items: Employee[];
  nextToken?: string | null;
};

@Injectable({
  providedIn: null,
})
export class EmployeeService {
  public loggedInEmployee = new BehaviorSubject<Employee | null>(null);

  constructor(public authService: AuthenticateService, public dataMapping: DataMappingService) {
    this.authService.loggedInUser.subscribe(async res => {
      if (res) {
        this.loggedInEmployee.next(await this.getEmployeeByUserId(res.id));
      }
    });
  }

  async getEmployeeByUserId(id: string): Promise<Employee | null> {
    const variables = {
      userId: id,
    };

    const response = await API.graphql<GraphQLQuery<EmployeeIdsByUserId>>({
      query: getEmployeeIdsByUserId,
      variables,
    });

    const employeeById = response.data?.employeesByUserId?.items[0];

    if (employeeById) {
      const employeeId = employeeById?.id;
      const variables: GetEmployeeQueryVariables = { id: employeeId };
      const employee = (
        await API.graphql<GraphQLQuery<GetEmployeeDetailQuery>>({
          query: getEmployeeDetail,
          variables,
        })
      ).data?.getEmployee;
      if (employee) {
        return {
          id: employee.id,
          user: this.dataMapping.mapUser(employee.user) || undefined,
          nik: employee.nik,
          status: employee.status,
          domicile: employee.domicile || '',
          organizationId: employee.organizationId || '',
          organizationUnitId: employee.organizationUnitId || '',
          organizationUnit: {
            name: employee?.organizationUnit?.name,
          },
        };
      }
    }
    return null;
  }

  async listEmployee(params: ListEmployeeParam = {}): Promise<ListEmployeeResult> {
    const variables: ListEmployeesWithAttributesQueryVariables = {};
    variables.limit = params.limit || 10;
    if (params.nextToken) variables.nextToken = params.nextToken;

    const res = (
      await API.graphql<GraphQLQuery<ListEmployeesWithAttributesQuery>>({
        query: listEmployeesWithAttributes,
        variables,
      })
    ).data?.listEmployees;

    const items = (res?.items || [])
      .filter(el => el !== null)
      .map(el => this.dataMapping.mapEmployee(el)) as Employee[];

    return {
      items,
      nextToken: res?.nextToken,
    };
  }

  async getEmployeeById(id:string):Promise<Employee |null> {
    const variables: GetEmployeeQueryVariables = { id };
    const employee = (
      await API.graphql<GraphQLQuery<GetEmployeeDetailQuery>>({
        query: getEmployeeDetail,
        variables,
      })
    ).data?.getEmployee;

    const res = this.dataMapping.mapEmployee(employee);

    return res;
  }
}
